import "./styles/globalStyles";
import "react-toastify/dist/ReactToastify.css";
import { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import Main from "./Routes/main";
import { isPublicSigningRoute, PUBLIC_SIGNING_ROUTE } from "./Routes/publicSigningRoute.mjs";
import reportWebVitals from "./reportWebVitals";
import store, { persistor } from "./Redux/store";
import { ClientDataProvider } from "./Components/ClientDashboard/shared/DataContext";
import { FooterProvider } from "./Components/common/shared/FooterContext";
import { ClientCommunicationWebSocketProvider } from "./contexts/ClientCommunicationWebSocketProvider";
import { ChecklistWebSocketProvider } from "./contexts/ChecklistWebSocketProvider";
import { GlobalModalProvider } from "./contexts/GlobalModalProvider";
import AuthSessionGate from "./Providers/AuthSessionGate";
import CookieConsentBanner from "./Components/common/CookieConsentBanner";
import PublicSigningPage from "./Modules/PublicSigningPage";
import RequestAccessPage from "./Modules/RequestAccessPage";
import { installImageFailureHandling } from "./Utils/imageFailureHandling.mjs";
import { callsEnabled } from "./features/calls/callsConfig";

const CallsProvider = lazy(() => import("./features/calls/CallsProvider"));

// perfectcase.com only serves the Request Access page; every other path goes there.
const PERFECTCASE_HOSTS = ["perfectcase.com", "www.perfectcase.com"];
const redirectingToRequestAccess =
  PERFECTCASE_HOSTS.includes(window.location.hostname) &&
  !/^\/request-access\/?$/.test(window.location.pathname);
if (redirectingToRequestAccess) {
  window.location.replace("/request-access" + window.location.search);
}

installImageFailureHandling(document);
const publicSigningRequest = isPublicSigningRoute(window.location.pathname);
const publicAccessRequest = /^\/request-access\/?$/.test(window.location.pathname);

const authenticatedApplication = (
  // <React.StrictMode>
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <AuthSessionGate>
        <ClientDataProvider>
          <ChecklistWebSocketProvider>
            <ClientCommunicationWebSocketProvider>
              <FooterProvider>
                <GlobalModalProvider>
                  {callsEnabled ? (
                    <Suspense fallback={null}>
                      <CallsProvider>
                        <Main />
                      </CallsProvider>
                    </Suspense>
                  ) : (
                    <Main />
                  )}
                  <CookieConsentBanner />
                </GlobalModalProvider>
                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="colored"
                />
              </FooterProvider>
            </ClientCommunicationWebSocketProvider>
          </ChecklistWebSocketProvider>
        </ClientDataProvider>
      </AuthSessionGate>
    </PersistGate>
  </Provider>
  // </React.StrictMode>
);

const publicSigningApplication = (
  <BrowserRouter>
    <Routes>
      <Route path={PUBLIC_SIGNING_ROUTE} element={<PublicSigningPage />} />
    </Routes>
  </BrowserRouter>
);

if (!redirectingToRequestAccess) {
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    publicAccessRequest ? (
      <RequestAccessPage />
    ) : publicSigningRequest ? (
      publicSigningApplication
    ) : (
      authenticatedApplication
    )
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// eslint-disable-next-line no-console
reportWebVitals(console.log());
